<template>
  <div class="c-partner-mapping-rule-card">
    <h2 class="c-partner-mapping-rule-card__title">{{ title }}</h2>
    <p v-if="subtitle" class="c-partner-mapping-rule-card__subtitle">
      {{ subtitle }}
    </p>
    <PartnerMappingSingleFilter
      ref="filterRef"
      :feed-id="feedId"
      :disabled="isDisabled"
      :hide-actions="!hasFilterActions"
      :has-apply-action="hasApplyAction"
      :is-removal-deferred="isRemovalDeferred"
      :is-adding-to-wishlist-on-save="isAddingToWishlistOnSave"
      :has-success-flash="hasSuccessFlash"
      :event-site="eventSite"
      @rule-removed="emit('ruleRemoved')"
    />
    <slot name="footer" />
  </div>
</template>

<script setup lang="ts">
import { EVENT_SITES, type EventSiteType } from '@crossbeam/itly';

import { computed, ref } from 'vue';

import PartnerMappingSingleFilter from './PartnerMappingSingleFilter.vue';

const {
  feedId,
  title,
  subtitle,
  isDisabled = false,
  hasFilterActions = true,
  hasApplyAction = true,
  isRemovalDeferred = false,
  isAddingToWishlistOnSave = false,
  hasSuccessFlash = true,
  eventSite = EVENT_SITES.PARTNER_MAPPING,
} = defineProps<{
  feedId: number;
  title: string;
  subtitle?: string;
  isDisabled?: boolean;
  hasFilterActions?: boolean;
  hasApplyAction?: boolean;
  isRemovalDeferred?: boolean;
  isAddingToWishlistOnSave?: boolean;
  hasSuccessFlash?: boolean;
  eventSite?: EventSiteType;
}>();

const emit = defineEmits<{ ruleRemoved: [] }>();

const filterRef = ref<InstanceType<typeof PartnerMappingSingleFilter> | null>(
  null,
);

const hasValidChanges = computed(
  () => filterRef.value?.hasValidChanges ?? false,
);
const hasFilter = computed(() => filterRef.value?.hasFilter ?? false);
const hasPendingRemoval = computed(
  () => filterRef.value?.hasPendingRemoval ?? false,
);
const sourceFilterExpressions = computed(
  () => filterRef.value?.sourceFilterExpressions ?? null,
);

function save() {
  return filterRef.value?.save() ?? Promise.resolve(null);
}

function removeRule() {
  return filterRef.value?.removeRule() ?? Promise.resolve(false);
}

defineExpose({
  save,
  removeRule,
  hasValidChanges,
  hasFilter,
  hasPendingRemoval,
  sourceFilterExpressions,
});
</script>

<style lang="pcss" scoped>
@reference "../../../../../css/theme.css";
.c-partner-mapping-rule-card {
  @apply bg-neutral-background-weak rounded-8 p-16 flex flex-col;
}

.c-partner-mapping-rule-card__title {
  @apply text-neutral-900 text-[1rem] font-bold mb-8;
}

.c-partner-mapping-rule-card__subtitle {
  @apply text-neutral-text-weak text-base mb-16;
}
</style>
